import { useState, useEffect } from "react";
import { toast } from "sonner";
import { StickyNote, Trash2 } from "lucide-react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";

function when(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("en-CA", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export const OrderNotesButton = ({ order, count, onCountChange }) => {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState([]);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get(`/admin/orders/${order.id}/notes`).then(({ data }) => setNotes(data)).catch(() => {});
  }, [open, order.id]);

  const add = async () => {
    const t = text.trim();
    if (!t || saving) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/orders/${order.id}/notes`, { text: t });
      const next = [...notes, data];
      setNotes(next);
      setText("");
      onCountChange(next.length);
      toast.success("Note pinned");
    } catch {
      toast.error("Failed to add note");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (noteId) => {
    try {
      await api.delete(`/admin/orders/${order.id}/notes/${noteId}`);
      const next = notes.filter((n) => n.id !== noteId);
      setNotes(next);
      onCountChange(next.length);
      toast.success("Note removed");
    } catch {
      toast.error("Failed to delete note");
    }
  };

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        data-testid={`admin-order-notes-button-${order.order_number}`}
        onClick={() => setOpen(true)}
        title="Pinned team notes (private)"
        className={`relative ${count > 0 ? "border-[#8C7B6B] text-[#8C7B6B]" : ""}`}
      >
        <StickyNote className="w-4 h-4" />
        {count > 0 && (
          <span
            data-testid={`admin-order-notes-count-${order.order_number}`}
            className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-[#8C7B6B] text-white text-[10px] font-bold flex items-center justify-center"
          >
            {count}
          </span>
        )}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="order-notes-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-wide">Notes — {order.order_number}</DialogTitle>
            <DialogDescription>Private team notes. Customers never see these.</DialogDescription>
          </DialogHeader>

          <div className="max-h-[40vh] overflow-y-auto space-y-3" data-testid="order-notes-list">
            {notes.length === 0 && (
              <p className="text-sm text-[#6E675F] text-center py-6" data-testid="order-notes-empty">
                No notes yet — pin one below so the team has context at a glance.
              </p>
            )}
            {notes.map((n) => (
              <div key={n.id} className="bg-[#FBF6E9] border border-[#E4D9BE] rounded-lg p-3" data-testid={`order-note-${n.id}`}>
                <p className="text-sm text-[#1A1A1A] whitespace-pre-line">{n.text}</p>
                <div className="flex items-center justify-between mt-2">
                  <p className="text-[11px] text-[#8C7B6B]">
                    {n.admin_name} · {when(n.created_at)}
                  </p>
                  <button
                    data-testid={`order-note-delete-${n.id}`}
                    onClick={() => remove(n.id)}
                    className="text-red-600 hover:text-red-800 transition-colors p-1"
                    aria-label="Delete note"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="border-t border-[#DDD7CF] pt-3">
            <Textarea
              data-testid="order-note-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="e.g. Customer asked to hold shipment until Friday…"
              rows={2}
              className="mb-2"
            />
            <Button
              data-testid="order-note-add-button"
              onClick={add}
              disabled={saving || !text.trim()}
              className="w-full bg-[#8C7B6B] text-white hover:bg-[#75665A]"
            >
              <StickyNote className="w-4 h-4 mr-1.5" /> {saving ? "Pinning…" : "Pin Note"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
